import { EventSessionWithDetail } from "@garther/interfaces";
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Progress,
  Table
} from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Copy, Ellipsis, Users } from "lucide-react";

interface EventSessionsProps {
  session: EventSessionWithDetail;
  onDuplicate: () => void;
  onViewAttendees: () => void;
}

export const EventSessionCard = ({ session, onDuplicate, onViewAttendees }: EventSessionsProps) => {
  const { formatDate, formatCredit } = useI18n();

  return (
    <Box key={session.id} className="space-y-4">
      {/* Session Header */}
      <div className="border-b border-border pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-responsive-md text-text-primary">
              {formatDate(session.startAt, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
              {session.endAt && (
                <>
                  {" - "}
                  {formatDate(session.endAt, {
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </>
              )}
            </p>
          </div>
          <div className="flex justify-end items-center gap-2">
            <span className="inline-block px-3 py-1 rounded-full bg-background-highlight text-responsive-sm font-medium text-text-primary">
              {session.status}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Ellipsis className="h-9 w-9 text-text-primary p-2 hover:bg-background-highlight hover:border hover:border-border-dark cursor-pointer rounded-md" />
              </DropdownMenuTrigger>

              <DropdownMenuContent align="right">
                <DropdownMenuItem icon={<Users size={18} strokeWidth={2} />} onClick={onViewAttendees}>
                  View Attendees
                </DropdownMenuItem>
                <DropdownMenuItem icon={<Copy size={18} strokeWidth={2} />} onClick={() => onDuplicate()}>
                  Duplicate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Session Stats */}
      <div className="space-y-4">
        <div>
          <p className="text-responsive-sm font-medium text-text-secondary">Capacity</p>
          <Progress value={(session.taken / session.cap) * 100} />
          <p className="text-responsive-sm text-text-secondary mt-1">
            {session.taken} / {session.cap} registered
          </p>
        </div>

        {/* Ticket Tiers */}
        {session.ticketTiers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-responsive-sm font-medium text-text-secondary">Ticket Tiers</h4>
            <Table
              data={session.ticketTiers}
              columns={[
                {
                  key: "title",
                  title: "Tier Name",
                  dataIndex: "title"
                },
                {
                  key: "price",
                  title: "Price",
                  dataIndex: "price",
                  formatter: (value: number) => formatCredit(value)
                },
                {
                  key: "capacity",
                  title: "Capacity",
                  dataIndex: "cap"
                },
                {
                  key: "taken",
                  title: "Registered",
                  dataIndex: "taken"
                },
                {
                  key: "available",
                  title: "Available",
                  render: (_, record) => record.cap - record.taken
                }
              ]}
            />
          </div>
        )}

        {/* Revenue */}
        <div className="bg-background-highlight rounded-lg p-3">
          <p className="text-responsive-sm font-medium text-text-secondary">Revenue</p>
          <p className="text-responsive-base font-semibold text-text-primary">
            {formatCredit(session.ticketTiers.reduce((total, tier) => total + tier.taken * tier.price, 0))}
          </p>
        </div>
      </div>
    </Box>
  );
};
